import {
  memo,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
} from 'react';

import dayjs from 'dayjs';
import isEqual from 'lodash/isEqual';

import { CAST_AI, TourKeys } from '@cast/constants';
import {
  BodyRow,
  BodyRowProps,
  Chip,
  Column,
  ColumnGroup,
  Icons,
  Skeleton,
  TableActionButton,
  TableBulkActions,
  TableHeader,
  Tooltip,
  useDrawer,
  makeActionsColumn,
  Typography,
  Box,
  Stack,
  tokens,
} from '@cast/design-system';
import {
  AgentStatus,
  Cluster,
  ComputedNodeStatus,
  Node,
  NodeStatus,
  OSEnum,
} from '@cast/types';
import { normalizePercentage, toNumOrZero } from '@cast/utils';

import { useSimulatedClusterDemoUi } from '@cast/infrastructure-demo';
import { NodeStatusChip, ResourceOfferingChip } from 'components/chips';
import { NodeInstanceTypeCell, NodeResourceUsageCell } from 'components/nodes';
import { TimeAgo } from 'components/time';
import { tableCacheKeys } from 'constants/tableCacheKeys';
import { useAbility } from '@cast/shared-ability-data-access';
import { TourStep } from 'core/tour';
import { useIsTourActive } from 'core/tour-v2/hooks/useIsTourActive';
import { makeNodeDetailsLink } from 'features/cluster/nodes/utils/link';
import { useIsHideDiscountsFeatureEnabled } from 'hooks/permissions';
import { useNavigate } from '@cast/infrastructure-organization';
import { getNodeRegion, isEdgeNode } from 'utils/node';

import { NodeCostCell } from './_components/NodeCostCell';
import { NodeCostCellBasePrice } from './_components/NodeCostCellBasePrice';
import { NodeNameCell } from './_components/NodeNameCell';
import { useNodeBulkActions } from './_hooks/useNodeBulkActions';
import { IssuesIcon } from '../../../../_components/IssuesIcon';
import { NodeActionsMenu } from '../../../_components/NodeActionsMenu';
import { NodePriceAdjustmentsTooltip } from '../../../_components/NodePriceAdjustmentsTooltip';
import { getMasterNodes, isTestNode } from '../../../_utils/common';
import { getGpuCount } from '../../../_utils/getGpuCount';
import { getNodeProvisioner } from '../../../_utils/getNodeProvisioner';
import { TestNodeChip } from '../../../components/TestNodeChip';
import { NODE_PROVISIONER_DISPLAY_NAMES } from '../../../constants/labels';
import { NodesContext } from '../../../NodesProvider';
import { nodeListTourId, tourSteps } from '../../../tourSteps';
import { PreparedNode } from '../../types';
import { NodeDetailsDrawer } from '../NodeDetailsDrawer';

import {
  useCluster,
  isAKSCluster,
  isClusterOnboardingPhase2,
  isClusterManaged,
  isKentEligibleCluster,
  isKentManagedCluster,
} from '@cast/shared-cluster-context-data-access';

import { FailedToLoad } from '@cast/shared-messages-ui';

import {
  NoData,
  NoResults,
  SearchableTable,
  SearchableTableRecordsCount,
  useSearchContext,
} from '@cast/shared-search-ui';

type Props = {
  error?: unknown;
  refetch?: () => void;
  hasStorageData?: boolean;
};

const AsyncCell = ({ children }: PropsWithChildren) => {
  const { isLoading, workloadsError } = useContext(NodesContext);

  if (isLoading) {
    return <Skeleton width={40} height={16} />;
  }

  if (workloadsError) {
    return <>-</>;
  }

  return <>{children}</>;
};

const useShouldShowAksTooltip = () => {
  const { cluster, isManaged } = useCluster();

  const isAks = isAKSCluster(cluster);
  const clusterIsConnecting = isClusterOnboardingPhase2(
    cluster.status,
    isManaged
  );

  const shouldShowTooltip = useCallback(
    (status?: NodeStatus | ComputedNodeStatus | string) => {
      return (
        isAks &&
        clusterIsConnecting &&
        status &&
        (status === NodeStatus.notReady || status === 'not-ready')
      );
    },
    [clusterIsConnecting, isAks]
  );

  return { shouldShowTooltip };
};

const getNodeProvisionerDisplayName = (node: Node) => {
  return NODE_PROVISIONER_DISPLAY_NAMES[getNodeProvisioner(node)];
};

const BodyRowWithTooltip = memo((rowProps: BodyRowProps<PreparedNode>) => {
  const { shouldShowTooltip } = useShouldShowAksTooltip();

  if (shouldShowTooltip(rowProps.row.state?.phase)) {
    return (
      <Tooltip
        testId="temporary-aks-node-tooltip"
        arrow
        placement="top"
        title={`This is a temporary node currently being used to set up ${CAST_AI}. It will be automatically removed once the operation finishes.`}
      >
        <div>
          <BodyRow {...rowProps} />
        </div>
      </Tooltip>
    );
  }

  return <BodyRow {...rowProps} />;
}, isEqual);

BodyRowWithTooltip.displayName = 'BodyRowWithTooltip';

type CanClickNodeDetailsArgs = {
  cluster: Cluster;
  isSimulatedClusterDemoUi: boolean;
  isOpTourActive: boolean;
};

const canClickNodeDetails = ({
  cluster,
  isSimulatedClusterDemoUi,
  isOpTourActive,
}: CanClickNodeDetailsArgs) => {
  if (isOpTourActive) {
    return false;
  }
  if (isSimulatedClusterDemoUi) {
    return true;
  }

  const isKentManaged = isKentManagedCluster(cluster);
  const isKentEligible = isKentEligibleCluster(cluster);

  if (
    (isClusterManaged(cluster) || isKentManaged || isKentEligible) &&
    cluster.agentStatus !== AgentStatus.NOT_RESPONDING
  ) {
    return true;
  }

  return false;
};

export const NodesTable = ({ error, refetch, hasStorageData }: Props) => {
  const canDelete = useAbility().can('delete', 'ClusterNodes');
  const hideDiscounts = useIsHideDiscountsFeatureEnabled();
  const isOpTourActive = useIsTourActive(TourKeys.OpinionatedPlatform);

  const searchContext = useSearchContext<PreparedNode>();
  const { cluster, isManaged, isCastAnywhere, isKentManaged } = useCluster();
  const nodesSelectable =
    (isManaged || isKentManaged) && !isCastAnywhere && !isOpTourActive;
  const isSimulatedClusterDemoUi = useSimulatedClusterDemoUi();
  const { open } = useDrawer();
  const navigate = useNavigate();

  const { shouldShowTooltip } = useShouldShowAksTooltip();
  const { selectedIds, setSelectedIds, rebalanceNodes, deleteNodes } =
    useNodeBulkActions();

  const masterNodes = useMemo(
    () => (searchContext?.data ? getMasterNodes(searchContext.data) : []),
    [searchContext]
  );

  const hasGpuData = useMemo(
    () => searchContext?.data?.some((node) => getGpuCount(node) > 0),
    [searchContext?.data]
  );

  const showDiscountCol = useMemo(
    () =>
      !hideDiscounts &&
      searchContext?.data?.some((n) => n.pricingInfo?.discounts?.length),
    [searchContext?.data, hideDiscounts]
  );

  const isNodeDetailsClickable = canClickNodeDetails({
    cluster,
    isSimulatedClusterDemoUi,
    isOpTourActive,
  });

  const handleSelected = (ids: string[]) => {
    if (searchContext?.data) {
      setSelectedIds(
        searchContext.data
          .filter((node) => ids.includes(node.id))
          .map((node) => node.id)
      );
    }
  };

  const handleOpenNodeDrawer = (node: Node) => {
    open({
      component: NodeDetailsDrawer,
      props: {
        node,
        cluster,
        masterNodesCount: masterNodes.length,
      },
    });
  };

  const handleNavigateToNodeDetails = (nodeId: string | undefined) => {
    if (!nodeId) {
      return;
    }

    navigate(
      makeNodeDetailsLink({
        clusterId: cluster.id,
        nodeId: nodeId,
      })
    );
  };

  return (
    <TourStep
      step={tourSteps.tableRow}
      type={nodeListTourId}
      text="Depending on the use case, you can quickly identify the most expensive node, or the one that's ready in the cluster."
      highlight="shadow"
      placement="top"
      popperDistance={-40}
      boxProps={{ flexGrow: 1 }}
    >
      <Box className="tour-highlight" height="100%" minHeight={400}>
        <SearchableTable<PreparedNode, string>
          cacheKey={tableCacheKeys.NODES.LIST}
          fillParentHeight
          onRowClick={(_, rowState) => {
            if (isNodeDetailsClickable) {
              handleOpenNodeDrawer(rowState.row);
            }
          }}
          BodyRowComponent={BodyRowWithTooltip}
          hasFailed={!!error}
          components={{
            noData: <NoData icon={<Icons.CastNode />} entity="node" />,
            noResults: <NoResults icon={<Icons.CastNode />} entity="node" />,
            failed: (
              <FailedToLoad title="Failed to load nodes" refresh={refetch} />
            ),
          }}
          {...(nodesSelectable && {
            selectedRows: selectedIds,
            onRowsSelected: handleSelected,
          })}
          outerHeader={
            <TableHeader
              recordsCount={
                <SearchableTableRecordsCount
                  count={selectedIds?.length}
                  title="node"
                />
              }
              bulkActions={
                <TableBulkActions visible={!!selectedIds.length}>
                  <TableActionButton
                    startIcon={<Icons.CastRebalancer />}
                    onClick={rebalanceNodes}
                    testId="node-bulk-action-rebalance"
                  >
                    Rebalance nodes
                  </TableActionButton>

                  <TableActionButton
                    startIcon={<Icons.TrashSimple />}
                    onClick={deleteNodes}
                    disabled={!canDelete}
                    disruptive
                    testId="node-bulk-action-delete-nodes"
                  >
                    Delete nodes
                  </TableActionButton>
                </TableBulkActions>
              }
            />
          }
          testId="node-list-table"
        >
          <Column<PreparedNode>
            id="name"
            minWidth={120}
            width={120}
            header={isKentManaged ? 'Name' : 'ID / Name'}
            cellWrapperSx={
              nodesSelectable ? { px: '8px' } : { pl: '16px', pr: '8px' }
            }
            renderCell={(node) => {
              return (
                <Stack direction="row" alignItems="center" gap={4}>
                  <NodeNameCell
                    node={node}
                    clickable={isNodeDetailsClickable}
                    onNameClick={() => handleNavigateToNodeDetails(node.id)}
                    copyToClipboardField={isKentManaged ? 'name' : 'id'}
                  />
                  {node && isTestNode(node) && <TestNodeChip />}
                </Stack>
              );
            }}
            resizable
            sortable
          />
          <Column<PreparedNode>
            id="createdAt"
            header="Created"
            minWidth={105}
            cellWrapperSx={{ pl: '16px', pr: '8px' }}
            renderCell={({ preparedTimeAgo }) => (
              <TimeAgo
                formattedDate={preparedTimeAgo.formattedDate}
                distance={preparedTimeAgo.distance}
              />
            )}
            sortable
            accessor={({ preparedTimeAgo }) => {
              return dayjs(preparedTimeAgo?.formattedDate).unix();
            }}
          />
          <Column<PreparedNode>
            id="instanceType"
            header="Type"
            cellWrapperSx={{ px: '8px' }}
            cellSx={{ display: 'flex', alignItems: 'center' }}
            renderCell={(node) => (
              <NodeInstanceTypeCell
                instanceType={node.instanceType}
                os={node.nodeInfo?.operatingSystem as OSEnum}
              />
            )}
            minWidth={95}
            sortable
            resizable
          />
          <Column<PreparedNode>
            id="region"
            header="Region"
            visible={false}
            cellWrapperSx={{ px: '8px' }}
            renderCell={(node) => {
              return (
                <Stack>
                  <Typography variant="inherit" noWrap>
                    {getNodeRegion(node)}
                  </Typography>
                  {isEdgeNode(node) && (
                    <Typography
                      variant="P10R"
                      color={tokens.theme.fg.structural.secondary}
                      mt={-4}
                      noWrap
                    >
                      Extended
                    </Typography>
                  )}
                </Stack>
              );
            }}
            accessor={(node) => getNodeRegion(node)}
            width={100}
            sortable
            resizable
          />
          <Column<PreparedNode>
            id="resourceOffering"
            minWidth={105}
            header="Res. offering"
            headerFullName="Resource offering"
            cellWrapperSx={{ px: '8px' }}
            bodyCellWrapperSx={{ position: 'relative' }}
            renderCell={(node, rowState) => {
              if (rowState.index === 1) {
                return (
                  <TourStep
                    step={tourSteps.resourceOfferingCell}
                    type={nodeListTourId}
                    text="You can verify the resource offering of each node. Fallback is a temporary on-demand node while spot is unavailable."
                    highlight="bubble"
                    placement="bottom-start"
                    beaconPosition={{
                      top: 12,
                      left: 20,
                    }}
                    boxProps={{ position: 'static' }}
                  >
                    <ResourceOfferingChip
                      resourceOffering={node?.resourceOffering}
                    />
                  </TourStep>
                );
              }

              return (
                <ResourceOfferingChip
                  resourceOffering={node?.resourceOffering}
                />
              );
            }}
            sortable
            accessor="resourceOffering"
          />

          <Column<PreparedNode>
            id="managedBy"
            header="Managed by"
            visible={false}
            cellWrapperSx={{ px: '8px' }}
            renderCell={(node) => getNodeProvisionerDisplayName(node)}
            accessor={(node) => getNodeProvisionerDisplayName(node)}
            width={100}
            sortable
            resizable
          />

          <ColumnGroup id="pricing-group" title="Pricing">
            {showDiscountCol && (
              <Column<PreparedNode>
                id="discount"
                width={56}
                header="Adjustment"
                cellWrapperSx={{ px: 8 }}
                renderCell={(node) =>
                  node.pricingInfo?.discounts?.length ? (
                    <NodePriceAdjustmentsTooltip
                      discounts={node.pricingInfo.discounts}
                    >
                      <Chip
                        size="sm"
                        sx={{
                          backgroundColor: 'green.600',
                          color: '#fff',
                          borderRadius: '16px',
                        }}
                        testId="discount-chip"
                      >
                        <Icons.Percent size="12px" />
                      </Chip>
                    </NodePriceAdjustmentsTooltip>
                  ) : null
                }
              />
            )}
            <Column<PreparedNode>
              id="price"
              minWidth={80}
              header="Node"
              cellWrapperSx={{ px: 8 }}
              renderCell={(node) =>
                !hideDiscounts && node.pricingInfo?.discounts?.length ? (
                  <NodeCostCellBasePrice
                    cost={node?.pricingInfo?.totalPrice}
                    basePrice={node?.pricingInfo?.totalRegularPrice}
                    testId="node-list-row-node-price"
                  />
                ) : (
                  <NodeCostCell
                    cost={node?.pricingInfo?.totalPrice}
                    testId="node-list-row-node-price"
                  />
                )
              }
              sortable
              sortingType="number"
              accessor={(node) => node?.pricingInfo?.totalPrice}
            />

            <Column<PreparedNode>
              id="pricePerCpu"
              minWidth={110}
              header="Cpu"
              infoTooltip="Normalized CPU cost, calculated by dividing the total instance cost by the number of CPUs"
              cellWrapperSx={{ px: '8px' }}
              renderCell={(node) => {
                return !hideDiscounts && node.pricingInfo?.discounts?.length ? (
                  <NodeCostCellBasePrice
                    cost={node.totalCpuPrice}
                    basePrice={node.baseCpuPrice}
                    testId="node-list-row-cpu-price"
                  />
                ) : (
                  <NodeCostCell
                    cost={node.totalCpuPrice}
                    testId="node-list-row-cpu-price"
                  />
                );
              }}
              sortable
              sortingType="number"
              accessor="totalCpuPrice"
            />
          </ColumnGroup>

          <ColumnGroup id="resource-requests" title="Resource Requests">
            <Column<PreparedNode>
              id="reqCpu"
              header="CPU"
              infoTooltip="CPU cores reserved by CPU requests of pods running on the node"
              cellWrapperSx={{ px: '8px' }}
              renderCell={(node) => (
                <NodeResourceUsageCell
                  available={<>{node?.cpu}</>}
                  value={normalizePercentage(toNumOrZero(node?.cpuPercentage))}
                />
              )}
              minWidth={120}
              sortable
              accessor={(node) =>
                normalizePercentage(toNumOrZero(node?.cpuPercentage))
              }
              sortingType="number"
            />

            <Column<PreparedNode>
              id="reqMem"
              header="Memory"
              infoTooltip="Memory reserved by Memory requests of pods running on the node"
              cellWrapperSx={{ px: '8px' }}
              renderCell={(node) => (
                <NodeResourceUsageCell
                  available={<>{node?.memory}</>}
                  value={normalizePercentage(
                    toNumOrZero(node?.memoryPercentage)
                  )}
                />
              )}
              minWidth={120}
              sortable
              accessor={(node) =>
                normalizePercentage(toNumOrZero(node?.memoryPercentage))
              }
              sortingType="number"
            />

            {hasGpuData && (
              <Column<PreparedNode>
                id="gpuCount"
                header="GPU"
                infoTooltip="Number of GPUs on the node"
                cellWrapperSx={{ px: '8px' }}
                minWidth={75}
                sortable
                sortingType="number"
                accessor={(node) => node.gpuCount}
              />
            )}

            {hasStorageData && (
              <Column<PreparedNode>
                id="reqStorage"
                header="EPH"
                infoTooltip="Ephemeral storage currently used by pods running on the node"
                cellWrapperSx={{ px: '8px' }}
                renderCell={(node) => (
                  <NodeResourceUsageCell
                    available={<>{node?.ephemeralStorage}</>}
                    value={
                      node?.ephemeralStoragePercentage !== undefined
                        ? normalizePercentage(node.ephemeralStoragePercentage)
                        : undefined
                    }
                  />
                )}
                minWidth={150}
                sortable
                accessor={(node) =>
                  normalizePercentage(
                    toNumOrZero(node?.ephemeralStoragePercentage)
                  )
                }
                sortingType="number"
              />
            )}
          </ColumnGroup>

          <Column<PreparedNode>
            id="workloads"
            header="Workloads"
            cellWrapperSx={{ px: '8px' }}
            renderCell={({ workloads }) => (
              <AsyncCell>{workloads?.length}</AsyncCell>
            )}
            width={75}
            sortable
            accessor="workloads.length"
          />

          <Column
            id="status"
            header="Status"
            cellWrapperSx={{ px: '8px' }}
            renderCell={({ state, isFailedToDrain }: PreparedNode) => {
              if (
                isFailedToDrain ||
                state?.phase === ComputedNodeStatus.DISRUPTED
              ) {
                return (
                  <Stack direction="row" alignItems="center" gap={2}>
                    <NodeStatusChip
                      status={state?.phase}
                      showTooltip={!shouldShowTooltip(state?.phase)}
                    />
                    <IssuesIcon />
                  </Stack>
                );
              }
              return (
                <NodeStatusChip
                  status={state?.phase}
                  showTooltip={!shouldShowTooltip(state?.phase)}
                />
              );
            }}
            width={115}
            sortable
            accessor="state.phase"
          />
          {nodesSelectable &&
            makeActionsColumn<PreparedNode>({
              width: 36,
              cellWrapperSx: { pl: 0, pr: 8 },
              action: (node) => (
                <NodeActionsMenu
                  cluster={cluster}
                  node={node}
                  masterNodesCount={masterNodes.length}
                />
              ),
            })}
        </SearchableTable>
      </Box>
    </TourStep>
  );
};
